import { ColorConfig, StatDefinition } from 'app/shared/collections/StatDefinition';
import { IndicatorIconType } from 'app/shared/image_url.service';
import { ImsObject } from './ims_object';

export const IMS_TYPE__VIEW = 'view';

export interface GenericViewComponentSchema {
    type: string;
}

// For example, show the mob at the location of the paddock the mob is assigned to
export interface ViewPropagateLocationSchema {
    type: 'propagate_location';
    display_name: string;
    description: string;
    // the reference to the relationship to follow
    relationship_table: string;
    relationship_field: string;
    // the reference to the location to get for the followed relationship
    src_position_table: string;
    src_position_field: string;
}

export interface ViewFollowRelationshipConfig {
    /**
     * The name of the dynamic stat schema which contains the relationship stats
     */
    relationship_table?: string;

    /**
     * The name of the field of the schema, which contains the foreign key to the related asset
     */
    relationship_field: string;

    /**
     * To show Paddock stats when clicking on a Cow, direction would be "forwards", because the relationship
     *  table specifies cow (asset_id) to paddock (paddock_id). So we're following the relationship
     *  normally.
     *
     * To show Cow stats when clicking on a Paddock, direction would be "backwards".
     *  because we're asking to find all Cows currently assigned to this Paddock, from the paddock's
     *  point of view: paddock (paddock_id) to cow (asset_id) of the relationship table
     *
     * To show Cow stats for a Paddock and Paddock stats for a cow, you can specify direction as "both"
     *   and put ["cow_stats", "paddock_stats"] as related tables.
     */
    direction: 'forwards' | 'backwards' | 'both';
}

/**
 * For example, when viewing a Cow in the infobox
 *  which has a dynamic stat pointing it to a Paddock
 *  show the Paddock Stats for that Paddock
 *
 * cow_assigned_paddock_stats
 *   asset_id (cow_id)
 *   paddock_id
 *
 *
 * paddock_stats
 *   asset_id (paddock_id)
 *   grain_volume
 *
 * cow_stats
 *   asset_id (cow_id)
 *   gender
 *
 * {
 *     "type": "infobox_follow_relationship",
 *     "relationship_table": "cow_assigned_paddock_stats",
 *     "relationship_field": "paddock_id",
 *     "related_tables": ["paddock_stats"],
 *     "direction": "forwards"
 * }
 *
 *
 * To show all related Cow stats when clicking on the Paddock
 * {
 *     "type": "infobox_follow_relationship",
 *     "relationship_table": "cow_assigned_paddock_stats",
 *     "relationship_field": "paddock_id",
 *     "related_tables": ["cow_stats"]
 *     "direction": "backwards",
 * }
 */
export interface ViewInfoboxFollowRelationship extends ViewFollowRelationshipConfig {
    type: 'infobox_follow_relationship';

    /**
     * A list of dynamic stat schemas names for which will be shown as child rows, can be empty
     *  to only show the details of the related asset itself, and not any data and null to use
     *  all dynamic stat schemas as child rows
     */
    related_tables: Array<string> | null;

    /**
     * Use this to limit the recursion
     */
    max_depth?: number | null;
}

/**
 For example, when choosing the Colour by Battery Voltage map option, if
 trying to find the colour of a Trailer type asset, follow the contains_stats
 schema to see if the Trailer contains any Battery type assets (upto a given depth or path)
 and ask the Battery assets for their Battery Voltage values, for aggregation, instead of
 seeing if the Trailer had any Battery Voltage values itself.

 {
 "type": "map_color_follow_relationship",
 "relationship_table": "contain_stats",
 "relationship_field": "contained_in_asset_id",
 "src_asset_type_names": ["Trailer"],
 "src_schemas": ["victron_battery_stats"],
 "dst_asset_type_names": ["Victron BMV"]
 }

 */
export interface ViewMapColorFollowRelationship {
    type: 'map_color_follow_relationship';

    /**
     * The name of the dynamic stat schema which contains the relationship stats
     *  will be recursive searched until an asset of the given destination type is found
     */
    relationship_table: string;

    /**
     * The name of the field of the schema, which contains the foreign key to the related asset
     */
    relationship_field: string;

    /**
     * A list of Asset Type names, for which will decide to follow the specified relationship
     *  to collect colour by stats, instead of looking at their own stats.
     *
     *  Eg: ["Trailer"]; [] = nothing, null = all
     */
    src_asset_type_names: Array<string> | null;

    /**
     * Eg: ["victron_battery_stats"]; [] = nothing, null = all
     */
    src_schemas: Array<string> | null;

    // TODO: I don't think this has been implemented
    /**
     * Through the given relationships/paths, filter by resultant assets
     *  by the given list of Asset Type names
     * Eg: ["Victron BMV"]
     */
    dst_asset_type_names: Array<string> | null;
}

/**
 *  Example:
 {
 "type": "center_panel_widgets",
 "widgets": [
 "centerAggTable",
 "centerQuicklist",
 "centerMapOptions",
 "centerRfPlanner",
 "centerZoneControls",
 "centerHeatMap",
 "dryMatterCharts"
 ]
 }
 *
 */
interface ViewCenterPanelWidgets {
    type: 'center_panel_widgets';
    widgets: Array<string>;
}

enum ViewComponentType {
    ConnectionLine = 'connection_line',
    PolygonVisualizer = 'polygon_visualizer',
    LineVisualizer = 'line_visualizer',
}

export interface ViewConnectionLineSchema {
    type: ViewComponentType.ConnectionLine;
    display_name: string;
    description: string;
    table: string;
    dest_asset_field: string;
    color_config_field?: string;
    color?: ColorConfig;
}

export interface ViewPolygonVisualizerSchema {
    type: ViewComponentType.PolygonVisualizer;
    display_name: string;
    description: string;
    table: string;
    polygon_field: string;
    color_config_field?: string;
    color?: ColorConfig;
}

export interface ViewLineVisualizerSchema {
    type: ViewComponentType.LineVisualizer;
    display_name: string;
    description: string;
    table: string;
    line_field: string;
    color_config_field?: string;
    color?: ColorConfig;
}

export interface ViewLabelSchema {
    type: 'asset_expression_label';
    /**
     * A user facing display name for this label
     */
    display_name: string;
    /**
     * A user facing description for this label
     */
    description: string;
    /**
     * The dynamic stat table name to use
     */
    table: string;
    /**
     * A javascript expression
     */
    text_expression: string;
}

export interface ViewIndicatorSchema extends ViewFollowRelationshipConfig {
    type: 'expression_indicator';
    /**
     * A user facing display name for this indicator
     */
    display_name: string;
    /**
     * A user facing description for this indicator
     */
    description: string;
    /**
     * The dynamic stat table name to use
     */
    table: string;
    /**
     * A javascript expression
     */
    value_expression: string;
    asset_id_expression: string;
    /**
     * A javascript expression
     */
    text_expression: string;
    /**
     * A color config
     */
    color: ColorConfig;
    /**
     * The name on an indicator icon
     */
    icon?: IndicatorIconType;

    /**
     * Changes the expression properties from received a `stats` parameter, to a `stat` parameter
     * An indicator will be potentially (pending the expression) created per stat for the given asset,
     * rather than a single indicator per asset
     */
    indicator_per_stat?: boolean;
}

export interface ViewTagBasedIndicatorSchema extends ViewFollowRelationshipConfig {
    type: 'tag_based_indicator';
    /**
     * A user facing display name for this indicator
     */
    display_name: string;
    /**
     * A user facing description for this indicator
     */
    description: string;
    /**
     * The dynamic stat table name to use
     */
    table: 'location_stat';
    /**
     * The tag name to match on for this indicator
     */
    tag: string;
    /**
     * A color config
     */
    color: ColorConfig;
    /**
     * The name on an indicator icon
     */
    icon?: IndicatorIconType;
    /**
     * The text to be displayed next to the indicator, defaults to the tag name
     */
    text?: string;
}

export interface ViewVectorSchema {
    type: 'vector';
    /**
     * User facing name
     */
    display_name: string;
    /**
     * User facing description
     */
    description: string;

    /**
     * Name of the dynamic stat schema to use
     */
    table: string;

    /**
     * The field name to retrieve the heading.
     * Heading is the rotation from the north direction where a positive value is increasing eastward.
     * Measured in degrees, where 360 would describe 1 full rotation
     * Can be left null or blank - will default to 0 (pointing north/south)
     * Can be provided as a constant number as well
     * @example "stat.heading"
     */
    heading_expr?: string | number | null;

    /**
     * A JS expression which assumes a "stat" variable in scope and produces a pitch value.
     * Pitch is the rotation from the local xy-plane; where positive pitch angles are above the plane
     *  and negative pitch angles are below the plane.
     * Measured in degrees, where 360 would describe 1 full rotation
     * Can be left null or blank - will default to 0 (pointing flat on the x/y plane)
     * Can be provided as a constant number as well
     * @example "stat.pitch"
     */
    pitch_expr?: string | number | null;

    /**
     * A JS expression which assumes a "stat" variable in scope and produces a length value
     * Length is the distance from the center in metres.
     * Can be left null or blank - will default to 1m
     * Can be provide as a constant number as well
     * @example "Math.log(stat.magnitude) * 0.1"
     */
    length_expr?: string | number | null;

    /**
     * A JS expression which assumes a "stat" variable in scope and produces a relative width value
     * Can be left null or blank - will default to something reasonable
     * Can be provide as a constant number as well
     * @example "stat.magnitude || 10"
     */
    width_expr?: number;

    /**
     * The color config to use, where the provided value will be the final length of the vector
     */
    color?: ColorConfig;

    /**
     * The name of the field on the dynamic stat to use the color color config from.
     */
    color_config_field?: string;
}

export interface StatDisplayFilterSchema {
    type: 'stat_display_filter';
    table: string;
    filter_expression: string;
}

interface DefaultOptionConfig {
    type: 'default_options_config';
    config: any;
}

interface AssetTypeNameMapAllowList {
    type: 'asset_type_name_map_allow_list';
    asset_type_names: Array<string>;
}

function isViewComponentOfType<T extends GenericViewComponentSchema>(type: string) {
    return (component: GenericViewComponentSchema): component is T => component.type === type;
}

function componentsOfType<T extends GenericViewComponentSchema>(
    components: Array<GenericViewComponentSchema>,
    type: string,
): Array<T> {
    return Array.isArray(components) ? components.filter<T>(isViewComponentOfType(type)) : [];
}

export class View extends ImsObject {
    constructor(resource) {
        super(IMS_TYPE__VIEW, resource);
    }

    get name(): string {
        return this.resource.name;
    }

    get displayName(): string {
        return this.resource.display_name;
    }

    get description(): string {
        return this.resource.description;
    }

    get hidden(): boolean {
        return this.resource.hidden;
    }

    get tables(): { [tableName: string]: StatDefinition } {
        return this.resource.tables;
    }

    get indicators(): Array<ViewIndicatorSchema | ViewTagBasedIndicatorSchema> {
        return componentsOfType<ViewIndicatorSchema>(
            this.resource.components,
            'expression_indicator',
        ).concat(
            componentsOfType<ViewIndicatorSchema>(this.resource.components, 'tag_based_indicator'),
        );
    }

    get infoboxFollowRelationshipConfigs(): Array<ViewInfoboxFollowRelationship> {
        return componentsOfType<ViewInfoboxFollowRelationship>(
            this.resource.components,
            'infobox_follow_relationship',
        );
    }

    get labels(): Array<ViewLabelSchema> {
        return componentsOfType<ViewLabelSchema>(
            this.resource.components,
            'asset_expression_label',
        );
    }

    get vectors(): Array<ViewVectorSchema> {
        return componentsOfType<ViewVectorSchema>(this.resource.components, 'vector');
    }

    get connectionLines(): Array<ViewConnectionLineSchema> {
        return componentsOfType<ViewConnectionLineSchema>(
            this.resource.components,
            ViewComponentType.ConnectionLine,
        );
    }

    get locationPropagations(): Array<ViewPropagateLocationSchema> {
        return componentsOfType<ViewPropagateLocationSchema>(
            this.resource.components,
            'propagate_location',
        );
    }

    get polygonVisualizers(): Array<ViewPolygonVisualizerSchema> {
        return componentsOfType<ViewPolygonVisualizerSchema>(
            this.resource.components,
            ViewComponentType.PolygonVisualizer,
        );
    }

    get lineVisualizers(): Array<ViewLineVisualizerSchema> {
        return componentsOfType<ViewLineVisualizerSchema>(
            this.resource.components,
            ViewComponentType.LineVisualizer,
        );
    }

    get defaultOptionConfig(): DefaultOptionConfig | null {
        const configs = componentsOfType<DefaultOptionConfig>(
            this.resource.components,
            'default_options_config',
        );
        return configs ? configs[0] : null;
    }

    get assetTypeNameMapAllowLists(): Array<AssetTypeNameMapAllowList> {
        return componentsOfType<AssetTypeNameMapAllowList>(
            this.resource.components,
            'asset_type_name_map_allow_list',
        );
    }

    get statDisplayFilters(): Array<StatDisplayFilterSchema> {
        return componentsOfType<StatDisplayFilterSchema>(
            this.resource.components,
            'stat_display_filter',
        );
    }

    get mapColorFollowRelationship(): Array<ViewMapColorFollowRelationship> {
        return componentsOfType<ViewMapColorFollowRelationship>(
            this.resource.components,
            'map_color_follow_relationship',
        );
    }

    get centerPanelWidgetConfig(): ViewCenterPanelWidgets | null {
        return (
            componentsOfType<ViewCenterPanelWidgets>(
                this.resource.components,
                'center_panel_widgets',
            )[0] || null
        );
    }

    get components(): Array<GenericViewComponentSchema> {
        return this.resource.components || [];
    }
}
